/**
 * Category Aliases Map
 * Maps navigation category slugs to article category names (from WordPress imports and new articles)
 * This allows flexible matching when articles have different category names than the navigation structure
 */

export const categoryAliases: Record<string, string[]> = {
  // === OUTDOOR FURNITURE SECTION ===
  "garden-chairs": [
    "Garden Chairs",
    "Outdoor Chairs",
    "Patio Chairs",
    "Garden Seating",
  ],
  "garden-benches": [
    "Garden Benches",
    "Outdoor Benches",
    "Wooden Benches",
  ],
  "rattan-furniture": [
    "Rattan Furniture",
    "Rattan Garden Furniture",
    "Wicker Furniture",
  ],
  "swing-seats": [
    "Swing Seats",
    "Garden Swings",
    "Hanging Chairs",
    "Egg Chairs",
  ],
  "patio-sets": [
    "Patio Sets",
    "Garden Furniture Sets",
    "Outdoor Dining Sets",
  ],
  "bistro-sets": [
    "Bistro Sets",
    "Garden Bistro Sets",
    "Café Sets",
  ],
  "outdoor-dining-tables": [
    "Dining Tables",
    "Outdoor Dining Tables",
    "Garden Tables",
  ],
  "bbq-pizza-ovens": [
    "BBQ & Pizza Ovens",
    "BBQ",
    "Barbecue",
    "Pizza Ovens",
    "Outdoor Cooking",
  ],
  "parasols-gazebos": [
    "Parasols & Gazebos",
    "Garden Parasols",
    "Gazebos",
    "Garden Canopies",
  ],
  "garden-storage": [
    "Garden Storage",
    "Outdoor Storage",
    "Garden Sheds",
    "Storage Boxes",
  ],
  "fire-pits": [
    "Fire Pits",
    "Garden Fire Pits",
    "Chimineas",
    "Outdoor Heaters",
  ],
  "hot-tubs": [
    "Hot Tubs",
    "Inflatable Hot Tubs",
    "Garden Hot Tubs",
    "Lay-Z-Spa",
  ],

  // === REVIEWS SECTION ===
  "power-tools": [
    "Power Tools",
    "Power Tool Reviews",
    "Garden Machinery",
    "Battery Power",
    "Cordless Tools",
  ],
  "chainsaws": [
    "Chainsaws",
    "Chainsaw",
    "Chainsaw Reviews",
  ],
  "lawn-mowers": [
    "Lawn Mowers",
    "Lawn Mower Reviews",
    "Lawn Care",
    "Best Lawn Mowers",
    "Robot Lawn Mowers",
    "Cordless Lawn Mowers",
  ],
  "leaf-blowers": [
    "Leaf Blowers",
    "Leaf Blower Reviews",
    "Battery Power",
    "Best Leaf Blowers",
    "Cordless Leaf Blowers",
  ],
  "hedge-trimmers": [
    "Hedge Trimmers",
    "Hedge Trimmer Reviews",
    "Garden Machinery",
    "Best Hedge Trimmers",
    "Cordless Hedge Trimmers",
  ],
  "strimmers": [
    "Strimmers",
    "Strimmer Reviews",
    "Grass Trimmers",
    "Line Trimmers",
    "Best Strimmers",
  ],
  "garden-multi-tools": [
    "Garden Multi-Tools",
    "Garden Multi Tools",
    "Multi-Tools",
    "Multi Tools",
    "5-in-1 Multi-Tool",
    "5 in 1 Garden Multi Tool",
    "Petrol Multi-Tool",
    "Cordless Multi-Tool",
    "Best Garden Multi-Tools",
    "Garden Multi-Tool Reviews",
  ],
  "generators": [
    "Generators",
    "Generator Reviews",
    "Best Generators",
    "Portable Generators",
    "Power Generators",
  ],
  "air-compressors": [
    "Air Compressors",
    "Air Compressor",
    "Air Compressor Reviews",
    "Best Air Compressors",
    "Portable Air Compressors",
  ],
  "trenchers": [
    "Trenchers",
    "Trencher",
    "Trencher Reviews",
    "Best Trenchers",
    "Petrol Trenchers",
    "Walk-Behind Trenchers",
  ],
  "secateurs": [
    "Secateurs",
    "Pruning Shears",
    "Best Secateurs",
    "Hand Pruners",
  ],
  "spades-forks": [
    "Spades & Forks",
    "Garden Spades",
    "Garden Forks",
    "Digging Tools",
  ],
  "trowels": [
    "Trowels",
    "Hand Trowels",
    "Garden Trowels",
  ],
  "loppers": [
    "Loppers",
    "Lopping Shears",
    "Tree Loppers",
  ],
  "compost-bins": [
    "Compost Bins",
    "Composting",
    "Compost Tumblers",
    "Best Compost Bins",
  ],
  "greenhouses": [
    "Greenhouses",
    "Greenhouse Reviews",
    "Best Greenhouses",
    "Mini Greenhouses",
  ],
  "hoses": [
    "Garden Hoses",
    "Hose Pipes",
    "Hose Reels",
    "Best Garden Hoses",
  ],
  "wheelbarrows": [
    "Wheelbarrows",
    "Garden Carts",
    "Best Wheelbarrows",
  ],
  "cement-mixers": [
    "Cement Mixers",
    "Cement Mixer",
    "Cement Mixer Reviews",
    "Best Cement Mixers",
    "Concrete Mixers",
    "Concrete Mixer",
    "Mortar Mixers",
    "Belle Mixers",
  ],
  "wood-chippers": [
    "Wood Chippers",
    "Wood Chipper",
    "Wood Chipper Reviews",
    "Best Wood Chippers",
    "Chipper Shredders",
    "Garden Shredders",
    "Shredders",
  ],
  "electric-wood-chippers": [
    "Electric Wood Chippers",
    "Electric Wood Chipper",
    "Electric Garden Shredders",
  ],
  "petrol-wood-chippers": [
    "Petrol Wood Chippers",
    "Petrol Wood Chipper",
    "Petrol Garden Shredders",
  ],
  "garden-shredders": [
    "Garden Shredders",
    "Garden Shredder",
    "Shredders",
  ],
  "chipper-shredders": [
    "Chipper Shredders",
    "Chipper Shredder",
    "Wood Chippers",
  ],

  // === HOW-TO SECTION ===
  "planting": [
    "Planting Guides",
    "Planting",
    "How to Plant",
    "Planting Tips",
  ],
  "pruning": [
    "Pruning & Training",
    "Pruning",
    "How to Prune",
    "Pruning Guide",
  ],
  "propagation": [
    "Propagation",
    "Plant Propagation",
    "How to Propagate",
    "Taking Cuttings",
  ],
  "soil": [
    "Soil & Composting",
    "Soil",
    "Composting",
    "Soil Improvement",
    "Garden Soil",
  ],
  "spring": [
    "Spring Gardening",
    "Spring",
    "Spring Garden Tasks",
  ],
  "summer": [
    "Summer Gardening",
    "Summer",
    "Summer Garden Tasks",
  ],
  "autumn": [
    "Autumn Gardening",
    "Autumn",
    "Fall Gardening",
    "Autumn Garden Tasks",
  ],
  "winter": [
    "Winter Gardening",
    "Winter",
    "Winter Garden Tasks",
    "Winter Protection",
  ],
  "pests": [
    "Pest Control",
    "Pests",
    "Garden Pests",
    "Pests and Diseases",
  ],
  "diseases": [
    "Disease Prevention",
    "Diseases",
    "Plant Diseases",
    "Pests and Diseases",
  ],
  "weeds": [
    "Weed Control",
    "Weeds",
    "Weeding",
    "How to Control Weeds",
  ],
  "troubleshooting": [
    "Garden Troubleshooting",
    "Garden Tips",
    "Troubleshooting",
    "Garden Problems",
    "Garden Life",
  ],

  // === PLANTS SECTION ===
  "perennials": [
    "Perennials",
    "Perennial Plants",
    "Hardy Perennials",
  ],
  "annuals": [
    "Annuals",
    "Annual Plants",
    "Bedding Plants",
  ],
  "shrubs": [
    "Shrubs",
    "Garden Shrubs",
    "Flowering Shrubs",
  ],
  "trees": [
    "Trees",
    "Garden Trees",
    "Ornamental Trees",
  ],
  "climbers": [
    "Climbers",
    "Climbing Plants",
    "Vines",
  ],
  "bulbs": [
    "Bulbs",
    "Spring Bulbs",
    "Flower Bulbs",
  ],
  "indoor-plants": [
    "Indoor Plants",
    "House Plants",
    "Houseplants",
  ],
  "succulents": [
    "Succulents & Cacti",
    "Succulents",
    "Cacti",
    "Succulents & Cacti, Plants",
  ],
  "tropical": [
    "Tropical Plants",
    "Tropical",
    "Exotic Plants",
  ],
  "air-plants": [
    "Air Plants",
    "Tillandsia",
  ],
  "shade-plants": [
    "Plants for Shade",
    "Shade Plants",
    "Shade-Loving Plants",
  ],
  "container-plants": [
    "Plants for Containers",
    "Container Plants",
    "Pot Plants",
  ],
  "drought-tolerant": [
    "Drought-Tolerant",
    "Drought Resistant",
    "Water-Wise Plants",
  ],
  "wildlife-plants": [
    "Wildlife-Friendly",
    "Wildlife Plants",
    "Plants for Wildlife",
  ],

  // === GROW GUIDES SECTION ===
  "tomatoes": [
    "Tomatoes",
    "Growing Tomatoes",
    "How to Grow Tomatoes",
  ],
  "potatoes": [
    "Potatoes",
    "Growing Potatoes",
    "How to Grow Potatoes",
  ],
  "root-vegetables": [
    "Carrots & Root Veg",
    "Root Vegetables",
    "Carrots",
    "Parsnips",
    "Beetroot",
  ],
  "legumes": [
    "Beans & Peas",
    "Legumes",
    "Growing Beans",
    "Growing Peas",
  ],
  "salad": [
    "Salad Leaves",
    "Salad",
    "Lettuce",
    "Salad Crops",
  ],
  "strawberries": [
    "Strawberries",
    "Growing Strawberries",
  ],
  "raspberries": [
    "Raspberries",
    "Growing Raspberries",
    "Raspberry Canes",
  ],
  "apples-pears": [
    "Apples & Pears",
    "Apples",
    "Pears",
    "Fruit Trees",
  ],
  "citrus": [
    "Citrus",
    "Citrus Fruits",
    "Growing Citrus",
    "Lemons",
    "Oranges",
  ],
  "culinary-herbs": [
    "Culinary Herbs",
    "Herbs",
    "Kitchen Herbs",
    "Cooking Herbs",
  ],
  "medicinal-herbs": [
    "Medicinal Herbs",
    "Healing Herbs",
  ],
  "annual-herbs": [
    "Annual Herbs",
    "Basil",
    "Coriander",
  ],
  "perennial-herbs": [
    "Perennial Herbs",
    "Rosemary",
    "Thyme",
    "Sage",
  ],

  // === FLOWERS SECTION ===
  "spring-flowers": [
    "Spring Flowers",
    "Spring Blooms",
  ],
  "summer-flowers": [
    "Summer Flowers",
    "Summer Blooms",
  ],
  "autumn-flowers": [
    "Autumn Flowers",
    "Fall Flowers",
  ],
  "winter-flowers": [
    "Winter Flowers",
    "Winter Blooms",
  ],
  "roses": [
    "Roses",
    "Rose Guides",
    "Growing Roses",
    "Rose Care",
  ],
  "dahlias": [
    "Dahlias",
    "Growing Dahlias",
  ],
  "tulips": [
    "Tulips",
    "Growing Tulips",
  ],
  "sunflowers": [
    "Sunflowers",
    "Growing Sunflowers",
  ],
  "cottage-garden": [
    "Cottage Garden",
    "Cottage Garden Plants",
  ],
  "wildflowers": [
    "Wildflower Meadow",
    "Wildflowers",
    "Wild Flowers",
  ],
  "pollinator-garden": [
    "Pollinator Garden",
    "Bee-Friendly Plants",
    "Butterfly Plants",
  ],

  // === GARDEN DESIGN SECTION ===
  "small-gardens": [
    "Small Gardens",
    "Small Garden Design",
    "Compact Gardens",
  ],
  "large-gardens": [
    "Large Gardens",
    "Big Garden Design",
  ],
  "patios": [
    "Courtyards & Patios",
    "Patio Gardens",
    "Courtyard Gardens",
  ],
  "front-gardens": [
    "Front Gardens",
    "Front Garden Ideas",
  ],
  "modern-gardens": [
    "Modern Gardens",
    "Contemporary Gardens",
  ],
  "traditional-gardens": [
    "Traditional Gardens",
    "Classic Gardens",
  ],
  "japanese-gardens": [
    "Japanese Gardens",
    "Zen Gardens",
  ],
  "mediterranean": [
    "Mediterranean Style",
    "Mediterranean Gardens",
  ],
  "water-features": [
    "Water Features",
    "Garden Ponds",
    "Fountains",
  ],
  "paths": [
    "Garden Paths",
    "Pathways",
    "Garden Walkways",
  ],
  "raised-beds": [
    "Raised Beds",
    "Raised Garden Beds",
  ],
  "lighting": [
    "Garden Lighting",
    "Outdoor Lighting",
  ],

  // === SEASONAL GUIDE SECTION ===
  "january": [
    "January",
    "January Gardening",
    "What to do in January",
    "Winter Gardening January",
  ],
  "february": [
    "February",
    "February Gardening",
    "What to do in February",
    "Winter Gardening February",
  ],
  "march": [
    "March",
    "March Gardening",
    "What to do in March",
    "Spring Gardening March",
  ],
  "april": [
    "April",
    "April Gardening",
    "What to do in April",
    "Spring Gardening April",
  ],
  "may": [
    "May",
    "May Gardening",
    "What to do in May",
    "Spring Gardening May",
  ],
  "june": [
    "June",
    "June Gardening",
    "What to do in June",
    "Summer Gardening June",
  ],
  "july": [
    "July",
    "July Gardening",
    "What to do in July",
    "Summer Gardening July",
  ],
  "august": [
    "August",
    "August Gardening",
    "What to do in August",
    "Summer Gardening August",
  ],
  "september": [
    "September",
    "September Gardening",
    "What to do in September",
    "Autumn Gardening September",
  ],
  "october": [
    "October",
    "October Gardening",
    "What to do in October",
    "Autumn Gardening October",
  ],
  "november": [
    "November",
    "November Gardening",
    "What to do in November",
    "Autumn Gardening November",
  ],
  "december": [
    "December",
    "December Gardening",
    "What to do in December",
    "Winter Gardening December",
  ],
};

/**
 * Main category aliases - maps main category slugs to article category names
 */
export const mainCategoryAliases: Record<string, string[]> = {
  reviews: [
    "Reviews",
    "Product Reviews",
    "Garden Machinery",
    "Power Tools",
    "Battery Power",
    "Chainsaws",
    "Garden Tools",
    "Cement Mixers",
    "Cement Mixer",
    "Wood Chippers",
    "Wood Chipper",
    "Garden Shredders",
  ],
  "wood-chippers": [
    "Wood Chippers",
    "Wood Chipper",
    "Wood Chipper Reviews",
    "Best Wood Chippers",
    "Chipper Shredders",
    "Garden Shredders",
    "Shredders",
  ],
  "how-to": [
    "How To",
    "Garden Tips",
    "Garden Life",
    "Gardening Tips",
    "Garden Advice",
    "Tips & Advice",
  ],
  plants: [
    "Plants",
    "Plant Guides",
    "Plant Care",
  ],
  "grow-guides": [
    "Grow Guides",
    "Growing Guides",
    "Vegetable Growing",
    "Fruit Growing",
    "Allotment",
  ],
  flowers: [
    "Flowers",
    "Flower Guides",
    "Flowering Plants",
  ],
  "garden-design": [
    "Garden Design",
    "Design Ideas",
    "Garden Ideas",
    "Garden Inspiration",
  ],
  "seasonal-guide": [
    "Seasonal Guide",
    "Monthly Gardening",
    "Seasonal Gardening",
    "What to do now",
    "Garden Calendar",
  ],
};

/**
 * Get all category names that should match a given subcategory slug
 */
export function getCategoryMatches(subcategorySlug: string): string[] {
  return categoryAliases[subcategorySlug] || [];
}

/**
 * Get all category names that should match a given main category slug
 */
export function getMainCategoryMatches(categorySlug: string): string[] {
  return mainCategoryAliases[categorySlug] || [];
}

/**
 * Get all aliases for a category (both direct name and slug-based lookups)
 */
export function getAllAliasesForCategory(categorySlug: string, categoryName: string): string[] {
  const directAliases = categoryAliases[categorySlug] || [];
  const mainAliases = mainCategoryAliases[categorySlug] || [];
  
  // Combine and deduplicate
  const allAliases = new Set([categoryName, ...directAliases, ...mainAliases]);
  return Array.from(allAliases);
}
